import { FC, useEffect, useMemo, useState } from "react";
import BillingFormData, { BillingSummaryData } from "./types/billing-form.data";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import BillingsNode from "../../common/types/billings-node";
import billingRecalculateRequestFactory from "./factory/billing-recalculate-request.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import billingSaveRequestFactory from "./factory/billing-save-request.factory";
import MapRoute from "../../../../common/components/map/types/map-route";
import MapMarker from "../../../../common/components/map/types/map-marker";
import Row from "../../../../common/components/grid/row";
import Column from "../../../../common/components/grid/column";
import MapComponent from "../../../../common/components/map/map.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import { useNavigate, useParams } from "react-router-dom";
import BillingCargoTaxiEditRouteParams from "../../common/routes/types/billing-cargo-taxi-edit-route-params";
import formValidationService from "../../../../common/utils/validation/form-validation.service";
import notificationService from "../../../../common/utils/notification/notification.service";
import billingRoutesHelper from "../../common/routes/billing-routes.helper";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import BillingsSummaryConfirmationComponent from "./billings-summary-confirmation.component";
import RelatedBillingsTaxiDriverComponent from "../common/related-billings/taxi-driver/related-billings-taxi-driver.component";
import RelatedBillingsTaxiTaxiComponent from "../common/related-billings/taxi-taxi/related-billings-taxi-taxi.component";
import BillingStatus from "../../types/billing-status";
import billingRouteDetailsHelper from "../../common/route-details/common/billings-route-details.helper";
import BillingRouteDetailsFormDataValidationResult, {
  NodeValidationResult,
} from "../../common/route-details/types/billings-route-details-form-data-validation-result";
import billingRouteDetailsDataValidationService from "../../common/route-details/common/billing-route-details-form-data-validation.service";
import BillingsRouteDetailsFormComponent from "../../common/route-details/billings-route-details-form.component";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import MessengerBillingType from "../../../../common/components/messenger/types/messenger-billing-type";
import billingsCargoTaxiHelper from "./billings-cargo-taxi.helper";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import TabsData from "../../../../common/components/tabs/common/types/tabs-data";
import BillingRouteDetailsComponent from "./billing-route-details/billing-route-details.component";
import TabsComponent from "../../../../common/components/tabs/tabs.component";
import OrderDetailsHistoryComponent from "../../../order/details/history/order-details-history.component";
import BillingsCargoTaxiContributions, {
  BillingsCargoTaxiContributionContractCorrectionPercentage,
  BillingsCargoTaxiContributionDiscountBeingLate,
  BillingsCargoTaxiContributionDiscountIncompatibleCar,
  BillingsCargoTaxiContributionDiscountOptimization,
  BillingsCargoTaxiContributionDiscountOther,
  BillingsCargoTaxiContributionDiscountType,
} from "../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingsToolsComponent from "../../common/tools/billings-tools.component";
import billingCargoTaxiContributionsDiscountValidationService from "../common/contributions/billings-cargo-taxi-contributions-discount-validation.service";
import BillingContributionValidationResult from "./types/billing-contributions-validation-result";
import billingsRouteDetailsHelper from "../../common/route-details/common/billings-route-details.helper";
import useApiCall from "../../common/hooks/use-api-call";
import BillingModel from "../../types/billing-model";
import billingsMapRouteFactory from "./factory/billings-map-route.factory";
import mapMarkerIconFactory from "../../../../common/components/map/marker/map-marker-icon.factory";
import dateService from "../../../../common/utils/date/date.service";
import useAbort from "../../../../common/hooks/use-abort";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import useBillingsCargoTaxiDetails from "../../../../common/services/billings/cargo-taxi/details/use-billings-cargo-taxi-details";
import BillingsCargoTaxiDetailsParams from "../../../../common/services/billings/cargo-taxi/details/billings-cargo-taxi-details-params";
import billingService from "../../../../common/services/billings/billings.service";
import GpsCargoOrderRecordsParams from "../../../../common/services/gps/cargo-order-records/gps-cargo-order-records-params";
import useGpsCargoOrderRecords from "../../../../common/services/gps/cargo-order-records/use-gps-cargo-order-records";
import GpsCargoOrderRecord from "../../../../common/services/gps/cargo-order-records/gps-cargo-order-records";
import useBillingsOrderCargoTaxiRecalculate from "../../../../common/services/billings/order/cargo-taxi/recalculate/use-billings-order-cargo-taxi-recalculate";

const BillingsCargoTaxiEditComponent: FC = () => {
  const { billingUuid } = useParams<BillingCargoTaxiEditRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [isBillingSavePending, setIsBillingSavePending] = useState(false);

  const [orderInternalId, setOrderInternalId] = useState("");

  const [contributionsValidationResults, setContributionsValidationResults] =
    useState<BillingContributionValidationResult>({ discount: [] });

  const [billingData, setBillingData] = useState<BillingFormData>();
  const [billingFormData, setBillingFormData] = useState<BillingFormData>();
  const [billingSummaryData, setBillingSummaryData] =
    useState<BillingSummaryData>();

  const [isSummaryConfirmationVisible, setIsSummaryConfirmationVisible] =
    useState(false);

  const [orderUuid, setOrderUuid] = useState("");

  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);
  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );

  const gpsCargoOrderRecords = useGpsCargoOrderRecords();
  const gpsCargoOrderRecordsAbort = useAbort();

  const [shouldShowCompletedMapRoutes, setShouldShowCompletedMapRoutes] =
    useState(false);

  const [hoveredMarker, setHoveredMarker] = useState<MapMarker | null>(null);

  const [completedRouteData, setCompletedRouteData] = useState<
    GpsCargoOrderRecord[]
  >([]);

  const [activeTab, setActiveTab] = useState(0);

  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);

  const billingsCargoTaxiDetails = useBillingsCargoTaxiDetails();
  const billingsCargoTaxiDetailsAbort = useAbort();

  const billingsCargoTaxiRecalculate = useBillingsOrderCargoTaxiRecalculate();
  const billingsCargoTaxiRecalculateAbort = useAbort();

  const cargoTaxiUpdateAbort = useAbort();

  const [formValidationResults, setFormValidationResults] =
    useState<BillingRouteDetailsFormDataValidationResult>({
      discountValue: formValidationService.defaultValidationResult,
      nodeValidations: [],
    });

  const [shouldShowOrderHistory, setShouldShowOrderHistory] = useState(false);

  const navigate = useNavigate();

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsCargoWithTaxiEdit.replace("#{orderInternalId}", orderInternalId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getCargoWithTaxiEditBreadcrumbs({
        billingUuid: billingUuid!,
        orderInternalId,
      });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, orderInternalId]);

  const onModalClose = () => {
    setIsSummaryConfirmationVisible(false);
  };

  const openModal = () => {
    setIsSummaryConfirmationVisible(true);
  };

  useEffect(() => {
    if (!billingUuid) {
      return;
    }

    loadBillingContributions();

    return billingsCargoTaxiDetailsAbort.revoke;
  }, [billingUuid]);

  const loadBillingContributions = () => {
    if (!billingUuid) {
      return;
    }

    const params: BillingsCargoTaxiDetailsParams = {
      billingUuid,
    };

    billingsCargoTaxiDetails.load(params, billingsCargoTaxiDetailsAbort.signal);
  };

  useEffect(() => {
    if (!orderUuid) return;

    const params: GpsCargoOrderRecordsParams = {
      orderUuid,
    };

    gpsCargoOrderRecords.load(params, gpsCargoOrderRecordsAbort.signal);

    return gpsCargoOrderRecordsAbort.revoke;
  }, [orderUuid]);

  useEffect(() => {
    if (!billingFormData || !shouldRetrieveRouteData) {
      return;
    }

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingFormData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];
      const lastResponseIndex = responses.length - 1;
      let lastResponseDuration = 0;

      responses.forEach((response, index) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }

        if (index === lastResponseIndex) {
          lastResponseDuration = response.routes[0].duration;
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      if (
        billingSummaryData &&
        billingSummaryData.contractDetails &&
        billingSummaryData.contractDetails.model !== BillingModel.AB
      ) {
        const lastBillingNodeIndex = billingFormData.billingNodes.length - 1;
        const secondToLastBillingNodePlannedDate =
          billingFormData.billingNodes[lastBillingNodeIndex - 1].plannedDate;

        if (secondToLastBillingNodePlannedDate) {
          const lastBillingNodePlannedDate = new Date(
            new Date(secondToLastBillingNodePlannedDate).getTime() +
              1000 * lastResponseDuration,
          );

          billingFormData.billingNodes[lastBillingNodeIndex].plannedDate =
            lastBillingNodePlannedDate;
          billingFormData.billingNodes[lastBillingNodeIndex].checkoutDate =
            lastBillingNodePlannedDate;

          setBillingData(billingFormData);
        }
      }

      setPlannedMapRoute(newMapRoute);
      setShouldRetrieveRouteData(false);
    });
  }, [shouldRetrieveRouteData]);

  const recalculateBillingFunction = (formData: BillingFormData) => {
    const params = billingRecalculateRequestFactory.createRecalculateRequest(
      orderUuid,
      formData,
    );

    billingsCargoTaxiRecalculate.load(
      params,
      billingsCargoTaxiRecalculateAbort.signal,
    );
  };

  const recalculateBilling = useApiCall(recalculateBillingFunction, 50);
  const deleteContributionRecalculateBilling = useApiCall(
    recalculateBillingFunction,
    250,
  );

  const navigateToListing = () => {
    navigate(billingRoutesHelper.getTaxiUnbilledOrderListingRoute());
  };

  const handleBillingSaveResponse = () => {
    notificationService.success(translations.successNotificationText);
    navigateToListing();
  };

  const handleBillingSaveError = () => {
    notificationService.error(translations.failureNotificationText);
  };

  useEffect(() => {
    if (!billingsCargoTaxiDetails.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsCargoTaxiDetails.data,
    );
    const billingSummaryData = billingDataFactory.createBillingSummaryData(
      billingsCargoTaxiDetails.data,
    );

    setBillingData(billingData);
    setBillingFormData(billingData);
    setBillingSummaryData(billingSummaryData);
    setOrderUuid(billingsCargoTaxiDetails.data.cargoOrder?.uuid);
    setOrderInternalId(
      String(billingsCargoTaxiDetails.data.cargoOrder?.humanId),
    );
    setShouldRetrieveRouteData(true);

    setFormValidationResults((current) => ({
      discountValue: current.discountValue,
      nodeValidations: billingData.billingNodes.map((x) => {
        return {
          haltingTime: formValidationService.defaultValidationResult,
          distance: formValidationService.defaultValidationResult,
          highwayCharge: formValidationService.defaultValidationResult,
          position: x.position,
        };
      }),
    }));

    const contributionsValidationResult: BillingContributionValidationResult = {
      discount: billingData.billingContributions.discount.map(
        (contribution, index) => ({
          position: index,
          type: formValidationService.defaultValidationResult,
          amount: formValidationService.defaultValidationResult,
          comment: formValidationService.defaultValidationResult,
          percentage: formValidationService.defaultValidationResult,
        }),
      ),
    };

    setContributionsValidationResults(contributionsValidationResult);
  }, [billingsCargoTaxiDetails.data]);

  const onCopyFromPlannedDistance = () => {
    const newBillingNodes = billingFormData?.billingNodes.map((node) => {
      node.distance = node.plannedDistance;

      return node;
    });

    const newBillingFormData: BillingFormData = {
      ...billingFormData!,
      billingNodes: newBillingNodes!,
    };

    setBillingFormData(newBillingFormData);

    const result = billingRouteDetailsHelper.validateAllFieldsByType(
      formValidationResults,
      newBillingFormData.billingNodes,
      "distance",
    );

    nodeValidationResults(result.nodeResult!);

    if (result.isAllFormValid) recalculateBilling(newBillingFormData);
  };

  useEffect(() => {
    setCompletedRouteData(gpsCargoOrderRecords.data);
    const gpsData: MapRoute = billingsMapRouteFactory.createCompletedMapRoute(
      gpsCargoOrderRecords.data,
    );

    setCompletedMapRoute(gpsData);
    setShouldShowCompletedMapRoutes(true);
  }, [gpsCargoOrderRecords.data]);

  useEffect(() => {
    if (completedRouteData.length > 0 && !gpsCargoOrderRecords.isLoading) {
      const gpsData: MapRoute =
        billingsMapRouteFactory.createCompletedMapRoute(completedRouteData);
      setCompletedMapRoute(gpsData);
    }
  }, [completedRouteData, gpsCargoOrderRecords.isLoading]);

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers = billingsMapMarkersFactory
      .createMapMarkers(billingFormData?.billingNodes ?? [])
      .map((marker) => ({ ...marker, isRouteMarker: true }));

    return mapMarkers;
  }, [billingFormData?.billingNodes]);

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const onMarkerHover = (marker: MapMarker | null) => {
    setHoveredMarker(marker);
  };

  const generateTimeBasedMarkers = (
    completedRoute: GpsCargoOrderRecord[],
    existingRouteMarkers: MapMarker[],
  ): MapMarker[] => {
    if (!completedRoute || completedRoute.length === 0) {
      return [];
    }

    const markers: MapMarker[] = [];
    const parseTime = (timestamp: Date | string): number =>
      new Date(timestamp).getTime();

    let startTime = parseTime(completedRoute[0].recordTime);
    let nextMarkerTime = startTime;

    let index = 0;

    completedRoute.forEach((currPoint) => {
      let currTime = parseTime(currPoint.recordTime);

      if (currTime >= nextMarkerTime) {
        nextMarkerTime = currTime + 60000;
        const date = new Date(currPoint.recordTime);

        if (dateService.checkIsValidDate(date)) {
          const formattedTime = dateService.formatTime(date);

          const newMarker = {
            coordinate: { latitude: currPoint.lat, longitude: currPoint.lon },
            icon: mapMarkerIconFactory.createGpsIcon(),
            title: formattedTime,
            isGpsRouteMarker: true,
            onMouseOver: () =>
              onMarkerHover({
                coordinate: {
                  latitude: currPoint.lat,
                  longitude: currPoint.lon,
                },
                icon: mapMarkerIconFactory.createGpsIcon(),
                title: formattedTime,
              }),
            onMouseOut: () => onMarkerHover(null),
            index: index,
          };

          index++;

          const isDuplicate = existingRouteMarkers.some(
            (routeMarker) =>
              routeMarker.coordinate.latitude ===
                newMarker.coordinate.latitude &&
              routeMarker.coordinate.longitude ===
                newMarker.coordinate.longitude &&
              routeMarker.isGpsRouteMarker,
          );

          if (!isDuplicate) {
            markers.push(newMarker);
          }
        }
      }
    });

    return markers;
  };

  const memoizedMarkers = useMemo(() => {
    if (!completedMapRoute) {
      return (
        <MapComponent
          key={
            hoveredMarker?.coordinate.latitude +
            "-" +
            hoveredMarker?.coordinate.longitude
          }
          markers={mapMarkers}
          autoFit
          routes={mapRoutes}
        />
      );
    }

    const gpsMarkers: MapMarker[] = generateTimeBasedMarkers(
      completedRouteData,
      mapMarkers,
    );

    return (
      <MapComponent
        key={
          hoveredMarker?.coordinate.latitude +
          "-" +
          hoveredMarker?.coordinate.longitude
        }
        markers={mapMarkers}
        gpsMarkers={gpsMarkers}
        autoFit
        routes={mapRoutes}
        shouldShowCompletedMapRoutes={shouldShowCompletedMapRoutes}
      />
    );
  }, [shouldShowCompletedMapRoutes, completedRouteData, mapMarkers, mapRoutes]);

  const onBillingDataSave = () => {
    if (!billingUuid) return;

    const areNodesValid =
      billingFormData?.billingNodes
        .map((node) => {
          return billingsRouteDetailsHelper.haltingTimeValidation(
            formValidationResults,
            node.haltingTime,
            node.position,
          );
        })
        .every((x) => x) ?? false;

    const areContributionsValid = validateContributions();

    const isFormValid =
      areContributionsValid && areNodesValid && validateDiscount();

    if (!isFormValid) {
      onModalClose();

      notificationService.error(translations.failureValidationNotificationText);
      return;
    }
    if (billingFormData) {
      setIsBillingSavePending(true);

      const params = billingSaveRequestFactory.createSaveBillingRequest(
        billingUuid,
        billingFormData,
      );

      billingService
        .updateCargoTaxiBillings(params, cargoTaxiUpdateAbort.signal)
        .then(handleBillingSaveResponse)
        .catch(handleBillingSaveError)
        .finally(() => setIsBillingSavePending(false));
    }
  };

  const validationOnBlur = (position: number, fieldName: string) => {
    type Node = keyof typeof node;

    const node = billingFormData?.billingNodes.find(
      (x) => x.position === position,
    );

    const fieldToValidate = node && node[fieldName as Node];

    const results = billingRouteDetailsHelper.validationOnBlur(
      fieldToValidate,
      formValidationResults,
      position,
      fieldName,
    );

    nodeValidationResults(results.nodeResult!);

    if (results.isAllFormValid) recalculateBilling(billingFormData!);
  };

  const nodeValidationResults = (node: NodeValidationResult[]) => {
    setFormValidationResults((curr) => ({
      ...curr,
      nodeValidations: node,
    }));
  };

  const validateDiscount = () => {
    const maximumDiscountValue = billingSummaryData?.contractDetails
      ?.discountLimit
      ? billingSummaryData?.contractDetails?.discountLimit
      : 0;

    const validationResult =
      billingRouteDetailsDataValidationService.validateDiscount(
        billingSummaryData?.contractCorrectionPercentage ?? 0,
        maximumDiscountValue,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      discountValue: validationResult,
    }));

    return validationResult.isValid;
  };

  const onBillingNodeChanged = (node: BillingsNode) => {
    const nodes = billingFormData?.billingNodes.filter(
      (x) => x.position !== node.position,
    )!;

    nodes.push(node);

    const sortedArray = nodes.sort((a, b) =>
      a.position < b.position ? -1 : 1,
    );

    setBillingFormData((current) => ({
      ...current!,
      billingNodes: sortedArray,
    }));
  };

  const onInputBlur = () => {
    recalculateBilling(billingFormData!);
  };

  useEffect(() => {
    if (!billingsCargoTaxiRecalculate.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsCargoTaxiRecalculate.data,
    );

    const newBillingSummaryData = billingDataFactory.createBillingSummaryData(
      billingsCargoTaxiRecalculate.data,
    );

    setBillingData(billingData);
    setBillingSummaryData(newBillingSummaryData);
  }, [billingsCargoTaxiRecalculate.data]);

  const translations =
    billingsTranslationsHelper.getCargoTaxiEditBillingsTranslations();

  const isSettleButtonVisible =
    billingSummaryData?.status !== BillingStatus.ACCEPTED;

  const messengerChannelsAvailability =
    billingsCargoTaxiHelper.getMessengerChannelAvailability();

  const validateContributionType = (
    type: BillingsCargoTaxiContributionDiscountType | null,
    position: number,
  ) => {
    const validationResult =
      billingCargoTaxiContributionsDiscountValidationService.validateType(type);

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,
      discount: contributionsValidationResults.discount.map((validation) =>
        validation.position === position
          ? {
              ...validation,
              type: validationResult,
            }
          : validation,
      ),
    };
    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionPercentage = (
    percentage: number | null,
    position: number,
  ) => {
    const validationResult =
      billingCargoTaxiContributionsDiscountValidationService.validatePercentage(
        percentage,
        billingSummaryData?.contractDetails?.discountLimit,
      );

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,

      discount: contributionsValidationResults.discount.map((validation) =>
        validation.position === position
          ? {
              ...validation,
              percentage: validationResult,
            }
          : validation,
      ),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionAmount = (
    amount: number | null,
    position: number,
  ) => {
    const validationResult =
      billingCargoTaxiContributionsDiscountValidationService.validateAmount(
        amount,
      );

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,
      discount: contributionsValidationResults.discount.map((validation) =>
        validation.position === position
          ? {
              ...validation,
              amount: validationResult,
            }
          : validation,
      ),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionComment = (
    comment: string | null,
    position: number,
  ) => {
    const validationResult =
      billingCargoTaxiContributionsDiscountValidationService.validateComment(
        comment ?? "",
      );

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,
      discount: contributionsValidationResults.discount.map((validation) =>
        validation.position === position
          ? {
              ...validation,
              comment: validationResult,
            }
          : validation,
      ),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const onContributionTypeChange = (
    position: number,
    type: BillingsCargoTaxiContributionDiscountType,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: billingFormData!.billingContributions.discount.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  type,
                } as BillingsCargoTaxiContributions)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
    recalculateBilling(formData);
  };

  const onContributionPercentageChange = (
    position: number,
    percentage: number | null,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: billingFormData!.billingContributions.discount.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  percentage,
                } as BillingsCargoTaxiContributions)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionAmountChange = (
    position: number,
    amount: number | null,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: billingFormData!.billingContributions.discount.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  amount,
                } as BillingsCargoTaxiContributions)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionCommentChange = (position: number, comment: string) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: billingFormData!.billingContributions.discount.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  comment,
                } as BillingsCargoTaxiContributions)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionTypeBlur = (position: number) => {
    const contributions = billingsCargoTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
    );

    const type = contributions[position].type;

    if (!type) {
      deleteContribution(position, false);
    } else {
      validateContributionType(type, position);
    }
  };

  const onContributionPercentageBlur = (position: number) => {
    const percentage = (
      billingFormData!.billingContributions.discount[
        position
      ] as BillingsCargoTaxiContributionContractCorrectionPercentage
    ).percentage;

    const isPercentageValid = validateContributionPercentage(
      percentage,
      position,
    );

    if (isPercentageValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const onContributionAmountBlur = (position: number) => {
    const contributions = billingsCargoTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
    );

    const amount = (
      contributions[position] as
        | BillingsCargoTaxiContributionDiscountOptimization
        | BillingsCargoTaxiContributionDiscountBeingLate
        | BillingsCargoTaxiContributionDiscountIncompatibleCar
        | BillingsCargoTaxiContributionDiscountOther
    ).amount;

    const isAmountValid = validateContributionAmount(amount, position);

    if (isAmountValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const onContributionCommentBlur = (position: number) => {
    const contributions = billingsCargoTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
    );

    const comment = (
      contributions[position] as
        | BillingsCargoTaxiContributionDiscountOptimization
        | BillingsCargoTaxiContributionDiscountBeingLate
        | BillingsCargoTaxiContributionDiscountIncompatibleCar
        | BillingsCargoTaxiContributionDiscountOther
    ).comment;

    const isCommentValid = validateContributionComment(comment, position);

    if (isCommentValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const validateContributions = () => {
    const contributionsValidation: BillingContributionValidationResult = {
      discount: billingFormData!.billingContributions.discount.map(
        (contribution, index) => {
          switch (contribution.type) {
            case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE: {
              return {
                position: index,
                comment: formValidationService.defaultValidationResult,
                amount: formValidationService.defaultValidationResult,
                type: billingCargoTaxiContributionsDiscountValidationService.validateType(
                  contribution.type,
                ),
                percentage:
                  billingCargoTaxiContributionsDiscountValidationService.validatePercentage(
                    contribution.percentage,
                  ),
              };
            }
            case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
            case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
            case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
            case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER: {
              return {
                position: index,
                comment:
                  billingCargoTaxiContributionsDiscountValidationService.validateComment(
                    contribution.comment,
                  ),
                amount:
                  billingCargoTaxiContributionsDiscountValidationService.validateAmount(
                    contribution.amount,
                  ),
                type: billingCargoTaxiContributionsDiscountValidationService.validateType(
                  contribution.type,
                ),
                percentage: formValidationService.defaultValidationResult,
              };
            }
            default: {
              return {
                position: index,
                type: formValidationService.defaultValidationResult,
                percentage: formValidationService.defaultValidationResult,
                amount: formValidationService.defaultValidationResult,
                comment: formValidationService.defaultValidationResult,
              };
            }
          }
        },
      ),
    };

    setContributionsValidationResults(contributionsValidation);

    return contributionsValidation.discount.every(
      (validation) =>
        validation.amount.isValid &&
        validation.comment.isValid &&
        validation.percentage.isValid &&
        validation.type.isValid,
    );
  };

  const deleteContribution = (
    position: number,
    recalculate: boolean = true,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: billingFormData!.billingContributions.discount.filter(
          (contribution, index) => index !== position,
        ),
      },
    };

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,
      discount: contributionsValidationResults.discount
        .filter((validation) => validation.position !== position)
        .map((validation, index) => ({ ...validation, position: index })),
    };

    setBillingFormData(formData);
    setContributionsValidationResults(validationResults);

    if (recalculate) {
      deleteContributionRecalculateBilling(formData);
    }
  };

  const addNewContribution = () => {
    const newContribution = billingsCargoTaxiHelper.getNewContribution();

    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        discount: [
          ...billingFormData!.billingContributions.discount,
          newContribution as BillingsCargoTaxiContributions,
        ],
      },
    };

    const validationResults: BillingContributionValidationResult = {
      ...contributionsValidationResults,
      discount: [
        ...contributionsValidationResults.discount,
        {
          position: contributionsValidationResults.discount.length,
          type: formValidationService.defaultValidationResult,
          percentage: formValidationService.defaultValidationResult,
          amount: formValidationService.defaultValidationResult,
          comment: formValidationService.defaultValidationResult,
        },
      ],
    };

    setBillingFormData(formData);
    setContributionsValidationResults(validationResults);
  };

  const tabsData: TabsData = useMemo(() => {
    if (!billingFormData) {
      return [];
    }

    return billingDataFactory.createTabsData(
      billingFormData.billingContributions,
      billingsCargoTaxiDetails.isLoading,
      deleteContribution,
      contributionsValidationResults,
      onContributionTypeChange,
      onContributionAmountChange,
      onContributionCommentChange,
      onContributionPercentageChange,
      onContributionTypeBlur,
      onContributionPercentageBlur,
      onContributionAmountBlur,
      onContributionCommentBlur,
    );
  }, [billingFormData, contributionsValidationResults, selectedAppLanguage]);

  const tabMaxContributions = useMemo(
    () => billingsCargoTaxiHelper.getMaxContributionsFromTabIndex(),
    [activeTab],
  );

  const onOrderHistoryButtonClick = () => {
    if (shouldShowOrderHistory) {
      setShouldShowOrderHistory(false);
      return;
    }

    setShouldShowOrderHistory(true);
  };

  const billingsCargoTaxiToolsProps = {
    shouldShowOrderHistory,
    onOrderHistoryButtonClick,
  };

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace(
            "#{orderInternalId}",
            orderInternalId,
          )}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  {memoizedMarkers}
                  <BillingsToolsComponent {...billingsCargoTaxiToolsProps} />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={billingsCargoTaxiDetails.isLoading}
                  contractDetails={billingSummaryData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={12}>
                    <BillingsRouteDetailsFormComponent
                      billingNodes={billingFormData?.billingNodes}
                      formValidationResults={
                        formValidationResults.nodeValidations
                      }
                      orderId={orderUuid}
                      onBillingNodeChanged={onBillingNodeChanged}
                      onInputBlur={onInputBlur}
                      onCopyFromPlannedDistance={onCopyFromPlannedDistance}
                      validationOnBlur={validationOnBlur}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <BillingRouteDetailsComponent
                  billingSummaryData={billingSummaryData}
                />
              </Column>
              {!!billingData && (
                <Column withPaddings>
                  <TabsComponent
                    data={tabsData}
                    onActiveTabChange={setActiveTab}
                  />
                  <div className="billings_contribution_add_button">
                    <ButtonComponent
                      onClick={addNewContribution}
                      type="success"
                      title={translations.contributionAddButtonTitle}
                      isDisabled={
                        tabsData[activeTab].totalTableRows >=
                        tabMaxContributions
                      }
                    >
                      {translations.contributionAddButtonLabel}
                    </ButtonComponent>
                  </div>
                </Column>
              )}
              <Column withPaddings>
                <CardComponent
                  classNames={{ root: "billing_details" }}
                  header={{ title: translations.billingSummary.summaryLabel }}
                >
                  <BillingDetailsComponent
                    billingSummaryData={billingSummaryData}
                  />
                </CardComponent>
              </Column>
              {!!isSettleButtonVisible && (
                <Column>
                  <ButtonComponent type="primary" onClick={openModal}>
                    {translations.submitLabel}
                  </ButtonComponent>
                </Column>
              )}
              <Column withPaddings>
                <RelatedBillingsTaxiDriverComponent
                  planEntryId={billingSummaryData?.planEntryId}
                  orderId={orderUuid}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiTaxiComponent orderId={orderUuid} />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            {!!billingSummaryData && (
              <CardComponent
                classNames={{
                  root: "billings_history_messenger_container",
                  content: "h-100",
                }}
              >
                {shouldShowOrderHistory ? (
                  <OrderDetailsHistoryComponent
                    orderUuid={orderUuid}
                    refetchFlag={false}
                  />
                ) : (
                  <MessengerComponent
                    channelsAvailability={messengerChannelsAvailability}
                    billingType={MessengerBillingType.CARGO}
                    billingUuid={billingUuid!}
                    orderUuid={orderUuid}
                    planEntryUuid={billingSummaryData.planEntryId}
                  />
                )}
              </CardComponent>
            )}
          </Column>
        </Row>
        <BillingsSummaryConfirmationComponent
          isVisible={isSummaryConfirmationVisible}
          onSubmit={onBillingDataSave}
          onClose={onModalClose}
          billingSummaryData={billingSummaryData!}
          isLoading={isBillingSavePending}
        />
      </div>
    </>
  );
};

export default BillingsCargoTaxiEditComponent;
